import React, { useState, useEffect } from 'react';
import { dashboardService } from '@/services/dashboard.service';
import { DashboardStats } from '@/types/dashboard.types';
import { Button } from '@/components/ui/Button';

export const Reports: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'year'>('month');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await dashboardService.getStats();
        setStats(response.data);
      } catch (err) {
        setError('Failed to load reports');
        console.error('Error fetching stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [dateRange]);

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `${(amount / 100000).toFixed(2)} L`;
    }
    return `${amount.toLocaleString('en-IN')}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Failed to load reports</h3>
        <p className="text-secondary-600 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Reports</h1>
          <p className="text-secondary-600 mt-1">View your performance metrics and analytics</p>
        </div>
        <div className="flex items-center gap-2">
          {(['today', 'week', 'month', 'year'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                dateRange === range
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-secondary-600 hover:bg-secondary-50 border border-secondary-200'
              }`}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Leads This Month</p>
              <p className="text-2xl font-bold text-secondary-900">{stats?.leads?.total_this_month || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Q-Leads</p>
              <p className="text-2xl font-bold text-secondary-900">{stats?.leads?.qleads_this_month || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Visits Completed</p>
              <p className="text-2xl font-bold text-secondary-900">{stats?.visits?.completed_this_month || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Wallet Balance</p>
              <p className="text-2xl font-bold text-secondary-900">{formatCurrency(stats?.wallet?.balance || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Lead Statistics */}
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Lead Statistics</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">This Month</span>
              <span className="font-semibold text-secondary-900">{stats?.leads?.total_this_month || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">Last Month</span>
              <span className="font-semibold text-secondary-500">{stats?.leads?.total_last_month || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">Q-Leads (Qualified)</span>
              <span className="font-semibold text-amber-600">{stats?.leads?.qleads_this_month || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-secondary-600">Conversion Rate</span>
              <span className="font-semibold text-secondary-900">{stats?.leads?.conversion_rate || 0}%</span>
            </div>
          </div>
        </div>

        {/* Visit Statistics */}
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Visit Statistics</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">This Month</span>
              <span className="font-semibold text-secondary-900">{stats?.visits?.total_this_month || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">Completed</span>
              <span className="font-semibold text-green-600">{stats?.visits?.completed_this_month || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">Scheduled (Upcoming)</span>
              <span className="font-semibold text-blue-600">{stats?.visits?.scheduled_upcoming || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-secondary-600">No Shows</span>
              <span className="font-semibold text-red-600">{stats?.visits?.no_shows_this_month || 0}</span>
            </div>
          </div>
        </div>

        {/* Earnings Summary */}
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Earnings Summary</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">Current Balance</span>
              <span className="font-semibold text-secondary-900">{formatCurrency(stats?.wallet?.balance || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-secondary-100">
              <span className="text-secondary-600">Commission This Month</span>
              <span className="font-semibold text-green-600">{formatCurrency(stats?.wallet?.commission_this_month || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-secondary-600">Pending Topups</span>
              <span className="font-semibold text-amber-600">{formatCurrency(stats?.wallet?.pending_topups || 0)}</span>
            </div>
          </div>
        </div>

        {/* Performance Chart Placeholder */}
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Performance Trend</h2>
          <div className="h-48 flex items-center justify-center bg-secondary-50 rounded-lg">
            <div className="text-center text-secondary-500">
              <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p>Chart visualization coming soon</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
